import React, { useEffect, useState, useRef } from 'react';
import { useHeader } from '../../contexts/HeaderContext';
import { SEO } from '../../components/SEO';
import { 
  AboutRoot, 
  TitleContainer, 
  TitleText, 
  HighlightText, 
  AboutImage, 
  TextRow,
  ContentSection,
  LeftColumn,
  RightColumn,
  DescriptionText,
  DetailText,
  DownloadButton,
  DownloadButtonText,
  DownloadIcon,
  ButtonWrapper,
  GrowthSection,
  GrowthText,
  MobileBreak,
  DesktopBreak,
  NumberSpan,
  FeatureSection,
  FeatureContainer,
  FeatureTextColumn,
  FeatureImageColumn,
  FeatureTitle,
  FeatureDescription,
  ProjectButton,
  ProjectButtonText,
  HistorySection,
  HistoryContainer,
  HistoryLeftColumn,
  HistoryRightColumn,
  HistoryTitle,
  HistorySubtitle,
  YearTabs,
  YearTab,
  HistoryContent,
  HistoryText,
  HistoryImage,
  GlobalScrollbarStyle,
  CenterImage,
  DotImage,
  SideImage,
  MarketingSection,
  HeadersRow,
  HeaderWrapper,
  ColumnsRow,
  Column,
  SectionTitle,
  HighlightSpan,
  FeatureContentWrapper
} from './styles';
import { useNavigate } from 'react-router-dom';

// historyData 타입 정의 추가
interface HistoryItem {
  content: string;
  image: string;
}

interface HistoryData {
  [year: string]: HistoryItem;
}

const About = () => {
  const { setIsTransparent } = useHeader();
  const navigate = useNavigate();
  const [activeYear, setActiveYear] = useState('2024');
  const yearTabsRef = useRef<HTMLDivElement>(null);
  
  // 히스토리 콘텐츠 애니메이션 상태
  const [historyContentVisible, setHistoryContentVisible] = useState(true);
  const historyContentRef = useRef<HTMLDivElement>(null);
  // 화면 너비 상태 추가
  const [isMobile, setIsMobile] = useState(false);
  const [isHistoryMobile, setIsHistoryMobile] = useState(false);
  const [circlePositions, setCirclePositions] = useState({
    left1: "30%",
    left2: "32%",
    left3: "34%",
    right1: "66%",
    right2: "68%",
    right3: "70%"
  });
  
  // 화면 크기 감지
  useEffect(() => {
    const calculatePositions = () => {
      // 모바일 체크
      const mobile = window.innerWidth <= 768;
      const historyMobile = window.innerWidth <= 1000;
      setIsMobile(mobile);
      setIsHistoryMobile(historyMobile);
      
      // 화면 너비에 따라 위치 조정
      const width = window.innerWidth;
      
      if (width <= 1440 && width > 1200) {
        setCirclePositions({
          left1: "calc(28% + 20px)",
          left2: "calc(30% + 20px)",
          left3: "calc(32% + 20px)",
          right1: "calc(68% - 30px)",
          right2: "calc(70% - 30px)",
          right3: "calc(72% - 30px)"
        });
      } else {
        setCirclePositions({
          left1: "calc(30% + 20px)",
          left2: "calc(32% + 20px)",
          left3: "calc(34% + 20px)",
          right1: "calc(66% - 30px)",
          right2: "calc(68% - 30px)",
          right3: "calc(70% - 30px)"
        });
      }
    };
    
    calculatePositions(); // 초기 로드시 계산
    
    window.addEventListener('resize', calculatePositions);
    return () => {
      window.removeEventListener('resize', calculatePositions);
    };
  }, []);

  useEffect(() => {
    setIsTransparent(false); // About 페이지에서는 불투명 헤더 사용
  }, [setIsTransparent]);

  // Replace old scroll animation with optimized version
  useEffect(() => {
    // Create the intersection observer with optimized settings
    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -10% 0px', // Less negative rootMargin to start animations earlier
      threshold: 0 // Trigger as soon as any part of the element is visible
    };

    // Use requestAnimationFrame to apply classList changes
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          requestAnimationFrame(() => {
            entry.target.classList.add('visible');
          });
        }
      });
    }, observerOptions);

    // Observe all animated sections
    const sections = [
      '.growth-section',
      '.feature-section',
      '.history-section'
    ];

    sections.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        observer.observe(element);
      });
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Add iOS scrolling optimization
  useEffect(() => {
    document.documentElement.style.overflowX = 'hidden';
    document.documentElement.style.overflowY = 'auto';
    document.documentElement.style.height = '100%';
    
    document.body.style.cssText = `
      overflow-x: hidden;
      overflow-y: auto;
      height: auto;
      -webkit-overflow-scrolling: touch;
    `;
    
    document.addEventListener('touchstart', () => {}, { passive: true });
    document.addEventListener('touchmove', () => {}, { passive: true });
    
    return () => {
      document.removeEventListener('touchstart', () => {});
      document.removeEventListener('touchmove', () => {});
    };
  }, []);

  const handleDownload = () => {
    // 회사 소개서 다운로드 로직
    try {
      const fileUrl = '/document/puzl.pdf';
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = 'puzl_company_profile.pdf';
      link.target = '_blank'; // Add target blank for better handling
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('다운로드 오류:', error);
      // 오류 발생 시 새 창에서 열기 시도
      window.open('/document/puzl.pdf', '_blank');
    }
  };

  const handleNavigateToWorks = () => {
    navigate('/works');
    window.scrollTo(0, 0);
  };

  const handleYearChange = (year: string) => {
    // Don't do anything if clicking the already active year
    if (year === activeYear) return;
    
    // First hide the content with animation
    setHistoryContentVisible(false);
    
    // After the fade-out animation finishes, change the content
    setTimeout(() => {
      setActiveYear(year);
      
      // After the content changes, show it with animation
      setTimeout(() => {
        setHistoryContentVisible(true);
      }, 50);
    }, 500); // This should match the transition duration in the CSS
  };

  // 연도별 히스토리 데이터에 타입 추가
  const historyData: HistoryData = {
    '2024': {
      content: '매출액 90억\n구글 프리미어 파트너 선정\n토스애즈 공식대행사 선정\n벤처기업인증\n마케팅 영향력을 고려한 광고 중개 시스템 특허 등록\n버거킹/유한킴벌리 등 다수 프로젝트 운영\n아이보스, 디지털 마케팅 임팩트 등 다수 연사 참여',
      image: '/images/about/history/toss-ads.png'
    },
    '2023': {
      content: '매출액 67억\n러닝스푼즈 그로스 마케팅 강의 출시\n관광기업 혁신바우처 공급기업 선정\n중소기업 혁신바우처 공급기업 선정\n수출바우처 공급기업 선정',
      image: '/images/about/history/learning-spoons.png'
    },
    '2022': {
      content: '매출액 18억\n기업부설연구소 설립\n청년창업사관학교 선정\n바이트댄스-틱톡 협업\n이커머스 자회사 법인 설립',
      image: '/images/about/history/kosme.png'
    },
    '2021': {
      content: '퍼즐코퍼레이션 설립',
      image: '/images/about/history/puzl-people.png'
    }
  };

  return (
    <AboutRoot className="about-page-container">
      <GlobalScrollbarStyle />
      <SEO 
        title="퍼즐코퍼레이션 | About - 기업 소개"
        description="퍼포먼스 마케팅과 IT 기술이 결합된 스케일업 전략을 제안하는 퍼즐코퍼레이션의 이야기. 창의적 사고와 전문성을 바탕으로 고객사의 성장을 함께 만들어갑니다."
        keywords="퍼즐코퍼레이션, PUZL, 회사소개, 기업소개, 스케일업, UGC마케팅, 퍼포먼스마케팅, 데이터기반"
        ogTitle="퍼즐코퍼레이션 | About - 기업 소개"
        ogDescription="퍼포먼스 마케팅과 IT 기술이 결합된 스케일업 전략을 제안하는 퍼즐코퍼레이션의 이야기. 창의적 사고와 전문성을 바탕으로 고객사의 성장을 함께 만들어갑니다."
        ogImage="/images/og-image2.png"
      />
      <TitleContainer>
        {isMobile ? (
          <>
            <TitleText>퍼즐코퍼레이션의 목표는</TitleText>
            <TitleText><HighlightSpan>클라이언트의 매출 성장</HighlightSpan>입니다.</TitleText>
          </>
        ) : (
          <TitleText>퍼즐코퍼레이션의 목표는<br className="pc-br" /><HighlightSpan>클라이언트의 매출 성장</HighlightSpan>입니다.</TitleText>
        )}
      </TitleContainer>
      
      {isMobile ? (
        // 모바일에서는 메인 이미지만 표시
        <div style={{
          width: '100vw',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0',
          backgroundColor: '#F8F8F8'
        }}>
          <img 
            src="/images/about/main.png" 
            alt="Puzl Corporation Main"
            style={{ 
              width: '100%',
              height: '112px',
              objectFit: 'contain'
            }}
          />
        </div>
      ) : (
        // PC에서는 기존 애니메이션 이미지
        <AboutImage>
          <SideImage 
            src="/images/about/animation-1/data.png" 
            alt="Data" 
            direction="left"
            style={{
              left: '15%',
              top: 'calc(50% - 32%)',
              transform: 'translateY(-50%)'
            }}
          />
          <DotImage 
            src="/images/about/animation-1/red-circle.png" 
            alt="Red Circle 1" 
            left={circlePositions.left1}
            top="calc(50% - 15%)" 
            delay="0.7s"
            group="left-circles"
          />
          <DotImage 
            src="/images/about/animation-1/red-circle.png" 
            alt="Red Circle 2" 
            left={circlePositions.left2}
            top="calc(50% - 15%)" 
            delay="0.7s"
            group="left-circles"
          />
          <DotImage 
            src="/images/about/animation-1/red-circle.png" 
            alt="Red Circle 3" 
            left={circlePositions.left3}
            top="calc(50% - 15%)" 
            delay="0.7s"
            group="left-circles"
          />
          <CenterImage 
            src="/images/about/animation-1/performance.png" 
            alt="Performance"
          />
          <DotImage 
            src="/images/about/animation-1/yellow-circle.png" 
            alt="Yellow Circle 1" 
            left={circlePositions.right1}
            top="calc(50% - 15%)" 
            delay="1.3s"
            group="right-circles"
          />
          <DotImage 
            src="/images/about/animation-1/yellow-circle.png" 
            alt="Yellow Circle 2" 
            left={circlePositions.right2}
            top="calc(50% - 15%)" 
            delay="1.3s"
            group="right-circles"
          />
          <DotImage 
            src="/images/about/animation-1/yellow-circle.png" 
            alt="Yellow Circle 3" 
            left={circlePositions.right3}
            top="calc(50% - 15%)" 
            delay="1.3s"
            group="right-circles"
          />
          <SideImage 
            src="/images/about/animation-1/creative.png" 
            alt="Creative" 
            direction="right"
            style={{
              right: '15%',
              left: 'auto',
              top: 'calc(50% - 32%)',
              transform: 'translateY(-50%)'
            }}
          />
          <MarketingSection>
            <HeadersRow>
              <HeaderWrapper className="growth">
                <SectionTitle color="orange" isHeader={true}>Growth Marketing</SectionTitle>
              </HeaderWrapper>
              <HeaderWrapper className="brand">
                <SectionTitle color="yellow" isHeader={true}>Brand Marketing</SectionTitle>
              </HeaderWrapper>
            </HeadersRow>
            
            <ColumnsRow>
              <Column>
                <SectionTitle color="gray">Search AD</SectionTitle>
                <SectionTitle color="gray">Display AD</SectionTitle>
                <SectionTitle color="gray">APP/WEB AD</SectionTitle>
              </Column>
              
              <Column>
                <SectionTitle color="gray">Data Analytics</SectionTitle>
                <SectionTitle color="gray">Data Management</SectionTitle>
                <SectionTitle color="gray">Retargeting AD</SectionTitle>
              </Column>
              
              <Column>
                <SectionTitle color="gray">UGC</SectionTitle>
                <SectionTitle color="gray">MCN</SectionTitle>
                <SectionTitle color="gray">Owned Media</SectionTitle>
              </Column>
              
              <Column>
                <SectionTitle color="gray">Creative Strategy</SectionTitle>
                <SectionTitle color="gray">Video/Image AD</SectionTitle>
                <SectionTitle color="gray">Brand Consulting</SectionTitle>
              </Column>
            </ColumnsRow>
          </MarketingSection>
        </AboutImage>
      )}
      
      <ContentSection>
        <LeftColumn>
          <DescriptionText>
            {isMobile ? (
              <>
                퍼즐코퍼레이션은 데이터를 기반으로 스케일업에<br className="desc-br" />
                필요한 맞춤형 전략을 제공하고 있습니다.
              </>
            ) : (
              <>
                퍼즐코퍼레이션은<br className="desc-br" />
                데이터를 기반으로 스케일업에 필요한<br className="desc-br" />
                맞춤형 전략을 제공하고 있습니다.
              </>
            )}
          </DescriptionText>
          <ButtonWrapper>
            <DownloadButton onClick={handleDownload}>
              <DownloadButtonText>회사 소개서 다운로드</DownloadButtonText>
              <DownloadIcon>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 16.5L7.5 12L8.55 10.95L11.25 13.65V4.5H12.75V13.65L15.45 10.95L16.5 12L12 16.5Z" fill="white"/>
                  <path d="M6 19.5C5.175 19.5 4.5 18.825 4.5 18V15H6V18H18V15H19.5V18C19.5 18.825 18.825 19.5 18 19.5H6Z" fill="white"/>
                </svg>
              </DownloadIcon>
            </DownloadButton>
          </ButtonWrapper>
        </LeftColumn>
        <RightColumn>
          <DetailText>
            퍼즐코퍼레이션은 퍼포먼스 마케팅부터 데이터 분석, UGC 마케팅을 활용하는 그로스 마케팅 전문 에이전시 입니다. 쇼핑몰부터 스타트업, 대기업까지 다양한 인사이트를 바탕으로 스케일업에 필요한 맞춤형 전략을 제공하며, 브랜드의 목표 달성을 위해 꼭 필요한 부분만 전략적으로 제안합니다.
            {'\n\n'}
            퍼즐코퍼레이션은 데이터 수집부터 마케팅 현황 체크까지 체계적인 온보딩 시스템을 제공합니다. 잘못된 운영으로 새어 나가는 광고비를 줄이고, 광고 퍼포먼스를 최적화하여, 광고비 대비 효과성이 좋은 데이터 기반의 운영 전략을 제공합니다. 실제 광고 집행 성과를 바탕으로 광고 소재를 유기적으로 수정하고 광고 세팅을 최적화합니다. 광고에서만 멈추지 않고 광고주와의 용어, 목표를 맞추며 협업합니다. 이 과정에서 불필요한 소통은 최소화하고 성과를 최대치를 올릴 수 있는 전략을 제안하며 지속적인 A/B 테스트를 진행합니다.
            {'\n\n'}
            퍼즐코퍼레이션의 목표는 클라이언트의 매출 성장이기에 크리에이티브한 콘텐츠와 브랜드에 맞는 캠페인 전략을 통해 성공적인 프로젝트를 약속합니다.
          </DetailText>
        </RightColumn>
      </ContentSection>
      <GrowthSection className="growth-section">
        <GrowthText>
          {isMobile ? (
            <>
              퍼즐코퍼레이션은 브랜드의<br />
              성장을 최우선 가치로 삼습니다.<br />
              "파트너의 성장이 곧 우리의<br />
              성장" 이라는 철학 아래,<br />
              2022년 18억 → 2023년 67억 →<br />
              2024년 90억 원의 비약적인<br />
              매출 성장을 이뤄냈습니다.<br />
              축적된 실전 경험과<br />
              성과 중심의 노하우를 바탕으로,<br />
              성장의 한 조각을<br />
              함께 맞춰드리겠습니다.
            </>
          ) : (
            <>
              퍼즐코퍼레이션은 브랜드의 성장을 최우선 가치로 삼습니다.<br />
              "파트너의 성장이 곧 우리의 성장" 이라는 철학 아래,<br />
              2022년 18억 → 2023년 67억 → 2024년 90억 원의 비약적인 매출 성장을 이뤄냈습니다.<br />
              축적된 실전 경험과 성과 중심의 노하우를 바탕으로 성장의 한 조각을 함께 맞춰드리겠습니다.
            </>
          )}
        </GrowthText>
      </GrowthSection>
      
      <FeatureSection className="feature-section feature-first">
        <FeatureContainer className="feature-first">
          <FeatureContentWrapper className="feature-content-wrapper">
            <FeatureTextColumn>
              <FeatureTitle>
                {isMobile ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <NumberSpan>01</NumberSpan>
                    <div>
                      실무 경험 5년차 이상의
                      <br />
                      PRO 마케터가 리딩합니다.
                    </div>
                  </div>
                ) : (
                  <>
                    <NumberSpan>01</NumberSpan>
                    <div>
                      실무 경험 5년차 이상의
                      <br />
                      PRO 마케터가 리딩합니다.
                    </div>
                  </>
                )}
              </FeatureTitle>
              <FeatureDescription>
                {isMobile ? (
                  <>온라인 마케팅의 범위는 너무나 다양하고 브랜드 카테고리, 목표, 컨셉별로 전략은 달라져야 하기에 다양한 경험치가 있는 PRO 마케터가 제안에 참여합니다.</>
                ) : (
                  <>온라인 마케팅의 범위는 너무나 다양하고<br />브랜드 카테고리, 목표, 컨셉별로 전략은 달라져야 하기에<br/> 다양한 경험치가 있는 PRO 마케터가 제안에 참여합니다.</>
                )}
              </FeatureDescription>
              <ProjectButton onClick={handleNavigateToWorks}>
                <ProjectButtonText>프로젝트 더보기</ProjectButtonText>
              </ProjectButton>
            </FeatureTextColumn>
            <FeatureImageColumn>
              <img 
                src="/images/about/animation-2/about01-3.gif" 
                alt="PRO 마케터" 
              />
            </FeatureImageColumn>
          </FeatureContentWrapper>
        </FeatureContainer>
      </FeatureSection>
      
      <FeatureSection className="feature-section feature-second">
        <FeatureContainer className="feature-second history-feature">
          <FeatureContentWrapper className="feature-content-wrapper">
            <FeatureImageColumn>
              <img 
                src="/images/about/animation-2/about02-2.gif" 
                alt="운영 히스토리 분석" 
              />
            </FeatureImageColumn>
            <FeatureTextColumn>
              <FeatureTitle>
                {isMobile ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <NumberSpan>02</NumberSpan>
                    <div>
                      운영했던 히스토리를 토대로
                      <br />
                      개선점을 찾아냅니다.
                    </div>
                  </div>
                ) : (
                  <>
                    <NumberSpan>02</NumberSpan>
                    <div>
                      운영했던 히스토리를 토대로
                      <br />
                      개선점을 찾아냅니다.
                    </div>
                  </>
                )}
              </FeatureTitle>
              <FeatureDescription>
                {isMobile ? (
                  <>진단도 없이 약을 처방하는 약사는 없습니다. 광고 운영도 똑같습니다. 운영했던 히스토리를 토대로 어떤 부분이 부족했고 개선이 필요한지에 대해 분석을 해야 유의미한 제안과 미팅이 이루어집니다.</>
                ) : (
                  <>진단도 없이 약을 처방하는 약사는 없습니다.<br />광고 운영도 똑같습니다.<br />운영했던 히스토리를 토대로 어떤 부분이 부족했고 <br/>개선이 필요한지에 대해 분석을 해야<br/> 유의미한 제안과 미팅이 이루어집니다.</>
                )}
              </FeatureDescription>
              <ProjectButton onClick={handleNavigateToWorks}>
                <ProjectButtonText>프로젝트 더보기</ProjectButtonText>
              </ProjectButton>
            </FeatureTextColumn>
          </FeatureContentWrapper>
        </FeatureContainer>
      </FeatureSection>

      <FeatureSection className="feature-section feature-third third-feature">
        <FeatureContainer className="feature-third">
          <FeatureContentWrapper className="feature-content-wrapper">
            <FeatureTextColumn>
              <FeatureTitle>
                {isMobile ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <NumberSpan>03</NumberSpan>
                    <div>
                      목표에 딱 맞는
                      <br />
                      맞춤 제안이 이루어 집니다.
                    </div>
                  </div>
                ) : (
                  <>
                    <NumberSpan>03</NumberSpan>
                    <div>
                      목표에 딱 맞는
                      <br />
                      맞춤 제안이 이루어 집니다.
                    </div>
                  </>
                )}
              </FeatureTitle>
              <FeatureDescription>
                일반적인 대행사는 브랜드에 필요한 맞춤 제안이 아닌 <br/>짜여진 템플릿에 맞춰 제안하는 경우가 많습니다. <br/>하지만 PUZL은 브랜드의 목적을 달성하기 위한 제안을 합니다.
              </FeatureDescription>
              <ProjectButton onClick={handleNavigateToWorks}>
                <ProjectButtonText>프로젝트 더보기</ProjectButtonText>
              </ProjectButton>
            </FeatureTextColumn>
            <FeatureImageColumn>
              <img 
                src="/images/about/animation-2/about03-1.gif" 
                alt="맞춤 제안" 
              />
            </FeatureImageColumn>
          </FeatureContentWrapper>
        </FeatureContainer>
      </FeatureSection>

      <HistorySection className="history-section">
        <HistoryContainer>
          <HistoryLeftColumn>
            <HistoryTitle>History</HistoryTitle>
            <HistorySubtitle>퍼즐코퍼레이션은 매년 빠르게 성장하고 있습니다.</HistorySubtitle>
          </HistoryLeftColumn>
          
          <HistoryRightColumn>
            <YearTabs 
              ref={yearTabsRef} 
              style={{
                marginTop: '0px',
                display: 'flex'
              }}
            >
              {Object.keys(historyData)
                .sort((a, b) => parseInt(b) - parseInt(a))
                .map((year) => (
                  <YearTab 
                    key={year} 
                    active={activeYear === year}
                    onClick={() => handleYearChange(year)}
                    style={{ 
                      transform: isHistoryMobile ? 'none' : (activeYear === year ? 'scale(1.05)' : 'scale(1)'),
                      transition: 'color 0.3s ease, transform 0.5s ease',
                      opacity: activeYear === year ? 1 : 0.6
                    }}
                  >
                    {year}
                  </YearTab>
                ))}
            </YearTabs>
            
            <HistoryContent 
              ref={historyContentRef} 
              style={{ 
                opacity: historyContentVisible ? 1 : 0,
                transform: historyContentVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.5s ease, transform 0.5s ease',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {isHistoryMobile ? (
                // 모바일 레이아웃: 이미지 위, 텍스트 아래
                <>
                  <HistoryImage 
                    src={historyData[activeYear].image} 
                    alt={`${activeYear} 히스토리`} 
                    style={{ 
                      marginTop: '0',
                      marginBottom: '0',
                      opacity: historyContentVisible ? 1 : 0,
                      transform: historyContentVisible ? 'translateY(0)' : 'translateY(20px)'
                    }}
                    onError={(e) => {
                      console.error(`Failed to load image: ${historyData[activeYear].image}`);
                      e.currentTarget.src = '/images/about/main.png'; // Fallback image
                    }}
                  />
                  <HistoryText style={{ paddingTop: '0', marginTop: '0' }}>
                    {historyData[activeYear].content.split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </HistoryText>
                </>
              ) : (
                // 데스크톱 레이아웃: 텍스트 위, 이미지 아래
                <>
                  <HistoryText style={{ paddingTop: '19px', marginTop: '0' }}>
                    {historyData[activeYear].content.split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </HistoryText>
                  <HistoryImage 
                    src={historyData[activeYear].image} 
                    alt={`${activeYear} 히스토리`} 
                    style={{ 
                      marginTop: '0',
                      marginBottom: '0',
                      opacity: historyContentVisible ? 1 : 0,
                      transform: historyContentVisible ? 'translateY(0)' : 'translateY(20px)'
                    }}
                    onError={(e) => {
                      console.error(`Failed to load image: ${historyData[activeYear].image}`);
                      e.currentTarget.src = '/images/about/main.png'; // Fallback image
                    }}
                  />
                </>
              )}
            </HistoryContent>
          </HistoryRightColumn>
        </HistoryContainer>
      </HistorySection>
    </AboutRoot>
  );
};

export default About;
